/**
 * Client dashboard data service.
 *
 * Two surfaces feed the post-onboarding client view:
 *   1. Balance growth chart — historical cumulative + dashed forward
 *      projection assuming the client keeps fulfilling their active
 *      commitments.
 *   2. Deposits timeline — last N fulfilled + next N scheduled, ordered
 *      chronologically so the client sees momentum across the
 *      transition from "you've done this" to "here's what's next".
 *
 * All computations live client-side: the backend exposes raw movements
 * and commitments; no aggregator endpoint is required for this view.
 * Upcoming dates are derived from the active commitment cadence.
 *
 * Brain-candy choices baked in:
 *   · The projection line shares the same gradient as historical but
 *     dashed and faded — visually says "if you keep going, you'll be
 *     here" without competing with the real data.
 *   · Past+future on the same timeline triggers the consistency /
 *     momentum bias (Duolingo / Strava streak pattern).
 *   · Default range is 6 months so growth is visible from day 1 of
 *     a fresh portfolio without padding empty space.
 */
import commitmentsService from './commitments.service';
import portfolioService from './portfolio.service';
import type { Cadence, Commitment } from '../types/commitments';
import type { LedgerMovement, ProductBalance } from '../types/finance';


export type TimeRange = '1M' | '6M' | '1Y' | 'ALL';

export interface ChartPoint {
  /** ISO date (UTC, day precision) used as X-axis key. */
  date: string;
  /** Cumulative balance up to that day if it's in the historical
   *  range; null in the future so the historical area stops cleanly. */
  historical: number | null;
  /** Same value at the boundary day (so the two areas join), then
   *  forward-only after "today". null in the past. */
  projected: number | null;
}

export interface TimelineEntry {
  /** Stable id for React keys + animation. */
  id: string;
  kind: 'completed' | 'upcoming';
  /** ISO date. For 'completed' it's `created_at`; for 'upcoming' it's
   *  the next expected occurrence per cadence. */
  date: string;
  amount: number;
  currency: string;
  product_name: string;
  cadence?: Cadence;
}

export interface ClientDashboardData {
  /** Today's total balance across all products, summed in their
   *  common currency. (QMI is single-currency MXN in practice; if a
   *  client ever has multi-currency, we'd render side-by-side cards
   *  instead of summing.) */
  currentBalance: number;
  currency: string;
  /** All historical points (no range filter applied — the chart
   *  component slices to the active range to keep the projection
   *  recompute cheap). */
  series: ChartPoint[];
  /** Last N fulfilled + next N scheduled, interleaved chronologically. */
  timeline: TimelineEntry[];
}

const DEFAULT_PROJECTION_HORIZON_MONTHS = 6;
const TIMELINE_COMPLETED_COUNT = 2;
const TIMELINE_UPCOMING_COUNT = 4;
// One page, capped at the backend's max page size (GET …/movements enforces
// size <= 200). Requesting more 422s and — because this call sits in the
// dashboard's Promise.all — took the whole dashboard data load down with it.
const MAX_MOVEMENTS = 200;

// ─── Helpers (exported for unit testing) ────────────────────────────

/** Reduce ledger movements into a day-by-day cumulative balance.
 *  Credits add, debits subtract. Days with no movement are NOT
 *  emitted (the chart interpolates between points anyway). */
export function buildHistoricalSeries(movements: LedgerMovement[]): ChartPoint[] {
  if (movements.length === 0) return [];
  const sorted = [...movements].sort(
    (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
  );
  let running = 0;
  const byDay = new Map<string, number>();
  for (const m of sorted) {
    const day = m.created_at.slice(0, 10); // YYYY-MM-DD
    const amount = Number(m.amount);
    if (Number.isNaN(amount)) continue;
    running += m.entry_type === 'credit' ? amount : -amount;
    byDay.set(day, running);
  }
  return Array.from(byDay.entries()).map(([date, balance]) => ({
    date,
    historical: balance,
    projected: null,
  }));
}

/** Given the last historical point and the active commitments, project
 *  forward by `horizonMonths` adding each commitment's contribution on
 *  its expected date. Returns ONLY the projected segment (excluding
 *  the boundary; the caller is responsible for stitching that point). */
export function projectForward(
  startBalance: number,
  startDate: Date,
  commitments: Commitment[],
  horizonMonths: number = DEFAULT_PROJECTION_HORIZON_MONTHS,
): ChartPoint[] {
  const horizon = new Date(startDate);
  horizon.setMonth(horizon.getMonth() + horizonMonths);

  // Collect every expected deposit between now and horizon, sorted.
  const events: { date: Date; amount: number }[] = [];
  for (const c of commitments) {
    if (c.status !== 'active') continue;
    const amount = Number(c.amount);
    if (Number.isNaN(amount)) continue;
    for (const occurrence of generateOccurrences(c, startDate, horizon)) {
      events.push({ date: occurrence, amount });
    }
  }
  events.sort((a, b) => a.date.getTime() - b.date.getTime());

  let running = startBalance;
  return events.map((e) => {
    running += e.amount;
    return {
      date: e.date.toISOString().slice(0, 10),
      historical: null,
      projected: running,
    };
  });
}

/** Enumerate every cadence occurrence in (from, until]. Excludes
 *  `from` itself so we don't double-count the boundary day. */
export function generateOccurrences(
  commitment: Commitment,
  from: Date,
  until: Date,
): Date[] {
  switch (commitment.cadence) {
    case 'one_time':  return oneTimeOccurrence(commitment, from, until);
    case 'monthly':   return monthlyOccurrences(commitment, from, until);
    case 'weekly':    return weeklyOccurrences(commitment, from, until);
    default:          return [];
  }
}

function oneTimeOccurrence(c: Commitment, from: Date, until: Date): Date[] {
  const d = new Date(c.start_date);
  return d > from && d <= until ? [d] : [];
}

function monthlyOccurrences(c: Commitment, from: Date, until: Date): Date[] {
  const dom = c.day_of_month ?? new Date(c.start_date).getUTCDate();
  // Start from the dom of `from`'s month; if that already passed,
  // jump to next month so we never emit a date <= from.
  const cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), dom));
  if (cursor <= from) cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  const out: Date[] = [];
  while (cursor <= until) {
    out.push(new Date(cursor));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}

function weeklyOccurrences(c: Commitment, from: Date, until: Date): Date[] {
  const targetDow = c.day_of_week ?? 1; // 1=Mon..7=Sun (ISO)
  const cursor = advanceToWeekday(from, targetDow);
  const out: Date[] = [];
  while (cursor <= until) {
    out.push(new Date(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return out;
}

/** Advance `start` forward day-by-day until its ISO weekday matches
 *  `targetDow` AND the cursor is strictly greater than the original.
 *  Centralised here so the weekly loop reads as one statement. */
function advanceToWeekday(start: Date, targetDow: number): Date {
  const cursor = new Date(start);
  // Loop bound: at most 7 iterations covers any starting position.
  for (let i = 0; i < 7; i += 1) {
    const isoDow = cursor.getUTCDay() === 0 ? 7 : cursor.getUTCDay();
    if (isoDow === targetDow && cursor > start) return cursor;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return cursor;
}

/** Compute the upcoming timeline entries from the active commitments.
 *  Returns the next N globally (across all commitments), not per
 *  commitment — so a weekly contract doesn't push out the monthly
 *  ones from view. */
export function buildUpcomingTimeline(
  commitments: Commitment[],
  n: number,
  now: Date = new Date(),
): TimelineEntry[] {
  const horizon = new Date(now);
  horizon.setMonth(horizon.getMonth() + 12); // 1y window is plenty
  const candidates: TimelineEntry[] = [];
  for (const c of commitments) {
    if (c.status !== 'active') continue;
    for (const occurrence of generateOccurrences(c, now, horizon)) {
      candidates.push({
        id: `upcoming:${c.id}:${occurrence.toISOString().slice(0, 10)}`,
        kind: 'upcoming',
        date: occurrence.toISOString(),
        amount: Number(c.amount),
        currency: c.currency_code,
        product_name: c.product_name,
        cadence: c.cadence,
      });
    }
  }
  candidates.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  return candidates.slice(0, n);
}

/** Pull the most recent N completed deposit movements as timeline
 *  entries so the client sees "what they've done" anchoring "what's
 *  next". Returned newest-first; the caller flips to oldest-first
 *  for the visual timeline. */
export function buildCompletedTimeline(
  movements: LedgerMovement[],
  n: number,
): TimelineEntry[] {
  return movements
    .filter((m) => m.category === 'deposit' && m.entry_type === 'credit')
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, n)
    .map((m) => ({
      id: `completed:${m.id}`,
      kind: 'completed',
      date: m.created_at,
      amount: Number(m.amount),
      currency: m.currency_code,
      product_name: m.product_name ?? 'Contribution',
    }));
}

function sumBalances(balances: ProductBalance[]): { total: number; currency: string } {
  // Multi-currency: if the client somehow has products in different
  // currencies, the chart-level sum stops making sense. We pick the
  // first currency as the display label and trust the (very common)
  // single-currency case — the design system has no "$ + €" pattern.
  let total = 0;
  for (const b of balances) total += Number(b.balance);
  return {
    total: Number.isNaN(total) ? 0 : total,
    currency: balances[0]?.currency_code ?? 'MXN',
  };
}

// ─── Service ────────────────────────────────────────────────────────
class ClientDashboardService {
  /** Single fetch that gathers everything the dashboard needs. Three
   *  REST calls in parallel — cheap. Stitches the chart series
   *  (historical + projection joined at "today") and the timeline. */
  async fetchAll(): Promise<ClientDashboardData> {
    const [balances, movementsRes, commitments] = await Promise.all([
      portfolioService.getMyBalance(),
      portfolioService.getMyMovements({ size: MAX_MOVEMENTS, page: 1 }),
      commitmentsService.listMine(['active']),
    ]);

    const { total: currentBalance, currency } = sumBalances(balances.items);
    const historical = buildHistoricalSeries(movementsRes.items);

    // Stitch boundary point so the two areas connect smoothly: take
    // today's date + today's balance, mark it as the last historical
    // AND first projected point.
    const today = new Date();
    const todayISO = today.toISOString().slice(0, 10);
    const boundary: ChartPoint = {
      date: todayISO,
      historical: currentBalance,
      projected: currentBalance,
    };
    const projection = projectForward(currentBalance, today, commitments);

    const completed = buildCompletedTimeline(movementsRes.items, TIMELINE_COMPLETED_COUNT)
      .reverse(); // oldest-first for visual top-down progression
    const upcoming = buildUpcomingTimeline(commitments, TIMELINE_UPCOMING_COUNT, today);

    return {
      currentBalance,
      currency,
      series: [...historical, boundary, ...projection],
      timeline: [...completed, ...upcoming],
    };
  }
}

export default new ClientDashboardService();
